'use client';

import { useSearchParams } from 'next/navigation';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatMessageItem } from '../types';
import { useSessionActions } from './use-session-actions';

const SHARE_SELECTION_QUERY_KEY = 'share';

// 一条消息可能映射到多条服务端 message id（例如 yolo 把 storyboard / answer 拆开）。
const getShareIds = (messages: ChatMessageItem[]) =>
  messages.flatMap((m) => m.shareMessageIds ?? (m.serverMessageId ? [m.serverMessageId] : []));

const clearShareSelectionQuery = () => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has(SHARE_SELECTION_QUERY_KEY)) return;
  url.searchParams.delete(SHARE_SELECTION_QUERY_KEY);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};

export const useShareSelection = ({
  activeSessionId,
  messages,
  setMessages,
  ready,
}: {
  activeSessionId: string | null | undefined;
  messages: ChatMessageItem[];
  setMessages: Dispatch<SetStateAction<ChatMessageItem[]>>;
  // 历史加载完成（!isHistoryBootstrapping && !historyLoading）才允许 ?share=1 自动进入。
  ready: boolean;
}) => {
  const searchParams = useSearchParams();
  const { sharingSession, shareSession, syncingShareIds, syncShareMessageIds } = useSessionActions();

  // 分享态：点 ChatHeader 分享按钮后进入，选中态期间 ChatInput 替换为多选确认条。
  const [isShareSelecting, setIsShareSelecting] = useState(false);
  const [selectedShareMessageIds, setSelectedShareMessageIds] = useState<Set<string>>(() => new Set());

  const selectableShareMessageIds = useMemo(() => Array.from(new Set(getShareIds(messages))), [messages]);

  const isAllShareMessagesSelected =
    selectableShareMessageIds.length > 0 && selectableShareMessageIds.every((id) => selectedShareMessageIds.has(id));

  const messageShareIdsList = useMemo(
    () => messages.map((m) => m.shareMessageIds ?? (m.serverMessageId ? [m.serverMessageId] : [])),
    [messages],
  );

  const selectedShareMessageCount = useMemo(
    () =>
      messageShareIdsList.filter((ids) => ids.length > 0 && ids.every((id) => selectedShareMessageIds.has(id))).length,
    [messageShareIdsList, selectedShareMessageIds],
  );
  const handleCancelShareSelection = useCallback(() => {
    setIsShareSelecting(false);
    setSelectedShareMessageIds(new Set());
    clearShareSelectionQuery();
  }, []);

  const handleStartShareSelection = useCallback(async () => {
    if (!activeSessionId || messages.length === 0) return;
    const selectableIds = await syncShareMessageIds(activeSessionId, messages, setMessages);
    setSelectedShareMessageIds(new Set(selectableIds));
    setIsShareSelecting(true);
  }, [activeSessionId, messages, setMessages, syncShareMessageIds]);

  const handleToggleShareSelection = useCallback(() => {
    if (isShareSelecting) {
      handleCancelShareSelection();
      return;
    }
    handleStartShareSelection();
  }, [handleCancelShareSelection, handleStartShareSelection, isShareSelecting]);

  const handleToggleShareMessage = useCallback((messageIds: string[]) => {
    setSelectedShareMessageIds((prev) => {
      const next = new Set(prev);
      const method = messageIds.every((id) => next.has(id)) ? 'delete' : 'add';
      for (const id of messageIds) next[method](id);
      return next;
    });
  }, []);

  const handleToggleAllShareMessages = useCallback(() => {
    setSelectedShareMessageIds(isAllShareMessagesSelected ? new Set() : new Set(selectableShareMessageIds));
  }, [isAllShareMessagesSelected, selectableShareMessageIds]);

  const handleConfirmShareSelection = useCallback(async () => {
    if (!activeSessionId || selectedShareMessageIds.size === 0 || sharingSession) return;
    await shareSession({ sessionId: activeSessionId, messageIds: Array.from(selectedShareMessageIds) });
    handleCancelShareSelection();
  }, [activeSessionId, handleCancelShareSelection, selectedShareMessageIds, shareSession, sharingSession]);

  // 通过 ?share=1 进入分享选中态：等历史加载完成 + 有消息时自动启动一次。
  // 用 ref 记录已自动触发过的 session：clearShareSelectionQuery 走 replaceState 不会刷新
  // useSearchParams，若仅靠 query 判定，handleStartShareSelection（依赖 messages）每次
  // setMessages 后重新生成都会再次进入，导致反复拉 history 死循环。
  const autoStartedSessionRef = useRef<string | null>(null);
  useEffect(() => {
    if (isShareSelecting) return;
    if (searchParams.get(SHARE_SELECTION_QUERY_KEY) !== '1') return;
    if (!activeSessionId || messages.length === 0 || !ready) return;
    if (autoStartedSessionRef.current === activeSessionId) return;
    autoStartedSessionRef.current = activeSessionId;
    handleStartShareSelection();
    clearShareSelectionQuery();
  }, [activeSessionId, handleStartShareSelection, isShareSelecting, messages.length, ready, searchParams]);

  // messages 变化时清掉已不在 selectable 集合里的选中项（例如某条消息被删/失败）。
  useEffect(() => {
    if (!isShareSelecting) return;
    const selectableSet = new Set(selectableShareMessageIds);
    setSelectedShareMessageIds((prev) => {
      const next = new Set(Array.from(prev).filter((id) => selectableSet.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [isShareSelecting, selectableShareMessageIds]);

  // 切 session 时退出分享态，避免选中项跨会话残留；同时重置自动触发标记，
  // 使切走再回到同一 session 时仍能通过 ?share=1 再次自动进入。
  useEffect(() => {
    setIsShareSelecting(false);
    setSelectedShareMessageIds(new Set());
    autoStartedSessionRef.current = null;
  }, [activeSessionId]);

  return {
    isShareSelecting,
    selectedShareMessageIds,
    selectableShareMessageIds,
    isAllShareMessagesSelected,
    selectedShareMessageCount,
    sharingSession,
    syncingShareIds,
    handleToggleShareSelection,
    handleToggleShareMessage,
    handleToggleAllShareMessages,
    handleConfirmShareSelection,
    handleCancelShareSelection,
  };
};
